import React, { useMemo, useState } from "react";
import { Box, Button, MenuItem, Stack, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exportVoicesResults } from "../../../Api/voicesOverview";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { dayKey } from "../Calendar/voicesCalendarDates";
import { useVoicesCampaigns } from "../../../Query/useVoicesCampaigns";
import { useVoicesResults, useVoicesSync } from "../../../Query/useVoicesOverview";
import Loading from "../../Common/Loading";
import VoicesCard from "../Common/VoicesCard";
import VoicesInput from "../Common/VoicesInput";
import { formatPostTime } from "../Posts/VoicesPostRows";
import { VoicesResultTiles, VoicesTopPosts, VoicesWeeklyChart } from "./VoicesResultBlocks";
import VoicesSyncButton from "./VoicesSyncButton";

const PERIODS = [7, 30, 90, 365];
const CUSTOM = "custom";
const MAX_DAYS = 366;

const daysAgo = (days: number) => {
  const day = new Date();
  day.setDate(day.getDate() - days);
  return dayKey(day);
};

const VoicesResults: React.FC = () => {
  const { t } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const [period, setPeriod] = useState<number | typeof CUSTOM>(90);
  const [customFrom, setCustomFrom] = useState(daysAgo(30));
  const [customTo, setCustomTo] = useState(dayKey(new Date()));
  const [searchParams] = useSearchParams();
  const [campaignId, setCampaignId] = useState<number | "">(
    Number(searchParams.get("campaign")) || "",
  );
  const [exporting, setExporting] = useState(false);
  const sync = useVoicesSync(true);

  const customDays =
    (new Date(`${customTo}T00:00:00`).getTime() - new Date(`${customFrom}T00:00:00`).getTime()) /
    86400000;
  const customValid = !!customFrom && !!customTo && customDays >= 0 && customDays <= MAX_DAYS;

  const filters = useMemo(() => {
    const range =
      period === CUSTOM
        ? { from: customFrom, to: customTo }
        : { from: daysAgo(period), to: dayKey(new Date()) };
    return { ...range, ...(campaignId ? { campaign_id: campaignId } : {}) };
  }, [period, customFrom, customTo, campaignId]);

  const { data: campaigns = [] } = useVoicesCampaigns();
  const { data: results, isLoading } = useVoicesResults(filters, period !== CUSTOM || customValid);

  const download = async () => {
    setExporting(true);
    try {
      await exportVoicesResults(filters);
    } catch {
      // the snack already shows what went wrong
    } finally {
      setExporting(false);
    }
  };

  const filterBar = (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 3 }}>
      <VoicesInput
        select
        size="small"
        value={period}
        onChange={(e) =>
          setPeriod(e.target.value === CUSTOM ? CUSTOM : Number(e.target.value))
        }
        sx={{ width: 180 }}
      >
        {PERIODS.map((days) => (
          <MenuItem key={days} value={days}>
            {t("results_lastDays", { count: days })}
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM}>{t("results_custom")}</MenuItem>
      </VoicesInput>
      {period === CUSTOM && (
        <>
          <VoicesInput
            type="date"
            size="small"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            error={!customValid}
            sx={{ width: 170 }}
          />
          <Typography variant="body2" sx={{ color: color.gray500 }}>
            –
          </Typography>
          <VoicesInput
            type="date"
            size="small"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            error={!customValid}
            sx={{ width: 170 }}
          />
          {!customValid && (
            <Typography variant="caption" sx={{ color: color.red700 }}>
              {t("results_customInvalid")}
            </Typography>
          )}
        </>
      )}
      <VoicesInput
        select
        size="small"
        value={campaignId}
        onChange={(e) => setCampaignId(e.target.value === "" ? "" : Number(e.target.value))}
        sx={{ width: 220 }}
      >
        <MenuItem value="">{t("calendar_allCampaigns")}</MenuItem>
        {campaigns.map((campaign) => (
          <MenuItem key={campaign.id} value={campaign.id}>
            {campaign.name}
          </MenuItem>
        ))}
      </VoicesInput>
      <Button
        variant="outlined"
        startIcon={<FileDownloadOutlinedIcon />}
        disabled={exporting || (period === CUSTOM && !customValid)}
        onClick={download}
        sx={{
          ml: { sm: "auto" },
          textTransform: "none",
          color: color.orange600,
          borderColor: color.orange600,
          "&:hover": { borderColor: color.orange600, backgroundColor: color.orange50 },
        }}
      >
        {t("results_export")}
      </Button>
    </Box>
  );

  if (isLoading || !results) {
    return (
      <>
        {filterBar}
        <Loading backgroundColor={color.gray50} color1={workspace.accent} color2={color.gray900} />
      </>
    );
  }

  const empty = (posts: number) =>
    posts === 0 && (
      <Typography variant="body2" sx={{ color: color.gray500, mt: 1 }}>
        {t("results_empty")}
      </Typography>
    );

  return (
    <>
      {filterBar}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Typography variant="caption" sx={{ color: color.gray600 }}>
          {results.freshness &&
            t("results_freshness", {
              figures: results.freshness.figures_as_of
                ? formatPostTime(results.freshness.figures_as_of)
                : "–",
              checked: results.freshness.checked_at
                ? formatPostTime(results.freshness.checked_at)
                : "–",
            })}
        </Typography>
        <VoicesSyncButton
          status={sync.status}
          onRefresh={() => sync.start.mutate({ kind: "all", manual: true })}
        />
      </Box>

      {results.workspace && (
        <VoicesCard title={t("results_allTitle")} subtitle={t("results_allSubtitle")}>
          <VoicesResultTiles totals={results.workspace.totals} />
          {empty(results.workspace.totals.posts)}
          <VoicesWeeklyChart weeks={results.workspace.weekly} />
        </VoicesCard>
      )}

      {results.pages && results.pages.length > 0 && (
        <VoicesCard title={t("results_pagesTitle")}>
          <Stack spacing={2}>
            {results.pages.map((page) => (
              <Box key={page.id}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {page.name}
                </Typography>
                <VoicesResultTiles totals={page} />
              </Box>
            ))}
          </Stack>
          <VoicesTopPosts posts={results.top_page_posts ?? []} title={t("results_topPagePosts")} />
        </VoicesCard>
      )}

      {results.mine && (
        <VoicesCard title={t("results_mineTitle")} subtitle={t("results_mineSubtitle")}>
          <VoicesResultTiles totals={results.mine.totals} />
          {empty(results.mine.totals.posts)}
          <VoicesWeeklyChart weeks={results.mine.weekly} />
          <VoicesTopPosts posts={results.mine.top_posts} title={t("results_topMine")} />
        </VoicesCard>
      )}
    </>
  );
};

export default VoicesResults;
